import { useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';

import {
  canSelectStore,
  createDefaultFilterForm,
  ERROR_TOAST_DURATION_MS,
  FilterForm,
  formatApiDate,
  ITEMS_PER_PAGE,
  LIST_QUERY_KEY,
  STORE_LIST_QUERY_KEY,
  SUCCESS_TOAST_DURATION_MS,
  TOAST_POSITION,
  ToastLocationState,
} from '@/pages/externals/mos-pos-resend/service';
import { routes } from '@/router';
import { getMosPosOrderList, getMosPosStoreList } from '@/services/MosPosOrder';
import { MosPosOrderListItem } from '@/services/MosPosOrder/type';
import { useMosPosResendListState } from '@/store/mos_pos_resend_list';

/**
 * @param enabled 一覧と店舗検索の候補を取得してよいかどうかです。呼び出し側のアクセス判定を渡します。
 *   アカウントの読み込みが完了するまでは false になるため、判定前のリクエストが
 *   発生しません。
 * @param isAdmin ログイン中のアカウントが管理者かどうかです。候補の再取得後に
 *   選択中の拠点を検索条件に残せるかどうかの判定 (canSelectStore) に使います。
 *   StoreFilter の固定表示と判定を揃えるため、StoreFilter に渡す値と同じ値を
 *   渡してください。
 */
export const useMosPosResendListPage = (enabled = true, isAdmin = false) => {
  const history = useHistory();
  const location = useLocation<ToastLocationState>();
  const toast = useToast();

  // 確定した検索条件とページは、確認画面から戻ったときなど一覧を開き直したときに
  // 復元するため store に置く
  const storedApplied = useMosPosResendListState((state) => state.applied);
  const page = useMosPosResendListState((state) => state.page);
  const setApplied = useMosPosResendListState((state) => state.setApplied);
  const setPage = useMosPosResendListState((state) => state.setPage);

  const defaultFilterForm = useMemo(() => createDefaultFilterForm(), []);
  /** 検索ボタンで確定した検索条件。react-query の queryKey に使う */
  const applied = storedApplied ?? defaultFilterForm;
  /** 入力中の検索条件。復元時は確定済みの条件から始める */
  const [form, setForm] = useState<FilterForm>(applied);

  const queryParams = useMemo(
    () => ({
      deliveryServiceId:
        applied.deliveryServiceId === ''
          ? undefined
          : Number(applied.deliveryServiceId),
      authStoreIds:
        applied.authStoreId === null ? undefined : [applied.authStoreId],
      orderDateStart: formatApiDate(applied.orderDateStart),
      orderDateEnd: formatApiDate(applied.orderDateEnd),
    }),
    [applied]
  );

  const { data, isLoading, error, isFetching } = useQuery(
    [LIST_QUERY_KEY, queryParams, page],
    () =>
      getMosPosOrderList({
        ...queryParams,
        page,
        pageSize: ITEMS_PER_PAGE,
      }),
    // 下のページ補正を取得し直した件数で判定するため、キャッシュが残るページへ
    // 移ったときも取得し直す
    { enabled, keepPreviousData: true, staleTime: 0 }
  );

  // 店舗検索の候補。一覧と同じアクセス判定でゲートする
  const {
    data: storeListData,
    isLoading: isLoadingStores,
    error: storeListError,
  } = useQuery([STORE_LIST_QUERY_KEY], () => getMosPosStoreList(), {
    enabled,
  });

  // 候補の再取得で選択中の拠点が候補から消えた場合や、候補が canSelectStore の
  // 条件を満たさず固定表示になった場合は、画面に出ない店舗条件が検索で送られない
  // よう選択を解除する。確定済みの条件は store から復元されるため、こちらも同じ
  // 基準で解除して取得し直す。読み込み中と取得失敗時は data が変わらないため、
  // 選択は維持される
  useEffect(() => {
    const stores = storeListData?.stores;
    if (stores === undefined) return;
    const isSelectable = (authStoreId: number) =>
      canSelectStore(stores.length, isAdmin) &&
      stores.some((store) => store.auth_store_id === authStoreId);
    setForm((prev) =>
      prev.authStoreId === null || isSelectable(prev.authStoreId)
        ? prev
        : { ...prev, authStoreId: null }
    );
    if (
      storedApplied !== null &&
      storedApplied.authStoreId !== null &&
      !isSelectable(storedApplied.authStoreId)
    ) {
      setApplied({ ...storedApplied, authStoreId: null });
      setPage(1);
    }
    // NOTE 候補とロールが変わったときだけ判定する
  }, [storeListData, isAdmin]);

  // 復元したページが件数の減少で範囲外になった場合は最終ページへ寄せる。
  // 該当なしの表示ではページャが出ず、他のページへ移れないため。
  // 判定は今の条件とページで取得に成功した件数だけで行う。取得中や取得失敗時の
  // data はキャッシュや直前の条件の件数のことがあり、件数が増えた後に開いたページを
  // 古い件数で戻すため。
  // ページは store の最新値で判定する。上の店舗の補正が同じ描画で 1 ページ目へ
  // 戻した場合に、補正前の件数で最終ページへ上書きしないため
  useEffect(() => {
    const currentPage = useMosPosResendListState.getState().page;
    if (data === undefined || isFetching || error) return;
    if (currentPage === 1) return;
    const lastPage = Math.max(1, Math.ceil(data.total / ITEMS_PER_PAGE));
    if (currentPage > lastPage) setPage(lastPage);
  }, [data, isFetching, error, page, setPage]);

  // 検索は検索ボタン押下時のみ実行する (入力の都度は実行しない)
  const handleSearch = useCallback(() => {
    setApplied(form);
    setPage(1);
  }, [form, setApplied, setPage]);

  const handleChangeDeliveryServiceId = useCallback((value: string) => {
    setForm((prev) => ({ ...prev, deliveryServiceId: value }));
  }, []);

  const handleChangeAuthStoreId = useCallback((value: number | null) => {
    setForm((prev) => ({ ...prev, authStoreId: value }));
  }, []);

  const handleChangeOrderDateStart = useCallback((date: Date) => {
    setForm((prev) => ({ ...prev, orderDateStart: date }));
  }, []);

  const handleChangeOrderDateEnd = useCallback((date: Date) => {
    setForm((prev) => ({ ...prev, orderDateEnd: date }));
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  /** 行クリック時は再送信可否に応じて対応する確認画面へ遷移する */
  const handleRowClick = useCallback(
    (row: MosPosOrderListItem) => {
      const path = row.can_resend
        ? routes.MosPosResendConfirm.path.replace(':orderId', row.order_id)
        : routes.MosPosCancelConfirm.path.replace(':orderId', row.order_id);
      history.push(path);
    },
    [history]
  );

  const handleResendClick = useCallback(
    (row: MosPosOrderListItem) => {
      history.push(
        routes.MosPosResendConfirm.path.replace(':orderId', row.order_id)
      );
    },
    [history]
  );

  const handleCancelClick = useCallback(
    (row: MosPosOrderListItem) => {
      history.push(
        routes.MosPosCancelConfirm.path.replace(':orderId', row.order_id)
      );
    },
    [history]
  );

  // 確認画面から戻ってきたときの成功トースト
  useEffect(() => {
    const succeeded = location.state?.toast;
    if (succeeded === undefined) return;

    toast({
      title: `${succeeded.webOrderId} を${
        succeeded.type === 'resend' ? '再送信' : '取消'
      }しました`,
      status: 'success',
      duration: SUCCESS_TOAST_DURATION_MS,
      isClosable: true,
      position: TOAST_POSITION,
    });
    // リロード時に再表示されないよう state を消す
    history.replace(routes.MosPosResendList.path, {});
    // NOTE 初回マウント時のみ判定する
  }, []);

  // API エラーのトースト
  useEffect(() => {
    if (error === null || error === undefined) return;
    toast({
      title: 'エラーが発生しました。時間をおいて再度お試しください。',
      status: 'error',
      duration: ERROR_TOAST_DURATION_MS,
      isClosable: true,
      position: TOAST_POSITION,
    });
    // NOTE エラーが変化したときだけトーストを出す
  }, [error]);

  // 店舗検索の候補取得エラーも一覧と同じ共通エラートーストで通知する
  useEffect(() => {
    if (storeListError === null || storeListError === undefined) return;
    toast({
      title: 'エラーが発生しました。時間をおいて再度お試しください。',
      status: 'error',
      duration: ERROR_TOAST_DURATION_MS,
      isClosable: true,
      position: TOAST_POSITION,
    });
    // NOTE エラーが変化したときだけトーストを出す
  }, [storeListError]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const stores = storeListData?.stores ?? [];

  return {
    form,
    page,
    items,
    total,
    stores,
    // enabled が false の間 react-query は idle になり isLoading が false になります。
    // そのまま返すと「候補 0 件」の固定表示を一瞬描画してしまうため、読み込み中として扱います。
    isLoadingStores: !enabled || isLoadingStores,
    hasStoreError: storeListError !== null && storeListError !== undefined,
    // enabled が false の間 react-query は idle になり isLoading が false になります。
    // そのまま返すと空状態を描画してしまうため、読み込み中として扱います。
    isLoading: !enabled || isLoading,
    isFetching,
    hasError: error !== null && error !== undefined,
    handlers: {
      handleSearch,
      handleChangeDeliveryServiceId,
      handleChangeAuthStoreId,
      handleChangeOrderDateStart,
      handleChangeOrderDateEnd,
      handlePageChange,
      handleRowClick,
      handleResendClick,
      handleCancelClick,
    },
  };
};
